import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Procedimiento operativo + compliance para dar de baja / migrar un tenant.
 * Importante: combina pasos técnicos (cambio de status, dump, borrado físico)
 * con obligaciones de la Ley 1581/2012 y Ley 1755/2015.
 */
export function DocsEliminarTenantPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Eliminar / migrar tenant
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Eliminar o migrar un tenant
        </h1>

        <div className="callout warn">
          <strong>Operación crítica.</strong> Toca datos personales bajo
          régimen de la Ley 1581/2012 y archivos públicos bajo Ley 594/2000.
          NO improvises — sigue el checklist completo.
        </div>

        <div className="callout">
          <strong>Tiempo total:</strong> ~30 días entre primera notificación
          y eliminación física. Mínimo 2 horas activas de trabajo del
          super_admin distribuidas en ese período.
        </div>

        {/* ---- Cuándo aplica ---- */}
        <Section title="1. Cuándo aplica este procedimiento">
          <Ul
            items={[
              "Contrato cancelado o no renovado por la alcaldía",
              "MiPueblo termina el contrato (mora, mal uso, incumplimiento de ToS)",
              "Alcaldía cambia a otro proveedor de PQRSDF y pide exportación + eliminación",
              "Cierre por reestructuración municipal (raro)",
              "Solicitud de eliminación bajo derecho al olvido — ojo: aplica a peticionarios individuales, no al tenant entero (ver punto 2)",
            ]}
          />
        </Section>

        {/* ---- Compliance ---- */}
        <Section title="2. Marco legal — qué podemos y NO podemos borrar">
          <Table
            head={["Dato", "Retención mínima legal", "Por qué"]}
            rows={[
              [
                "PQRSDFs y respuestas",
                "10 años desde radicación",
                "Documento público bajo Ley 594/2000 (Archivo General de la Nación). NO pueden borrarse aunque el tenant cierre — deben transferirse al archivo histórico del municipio.",
              ],
              [
                "Datos personales del peticionario (cédula, dirección, teléfono)",
                "Solo lo mínimo necesario para acreditar el trámite",
                "Ley 1581/2012 — principio de finalidad. Después de los 10 años se anonimiza, no se borra el PQRSDF entero.",
              ],
              [
                "Conversaciones de chat ciudadano",
                "5 años (recomendado por nuestra política, no normado)",
                "Sirven como evidencia de atención y cumplimiento SLA.",
              ],
              [
                "Audit log (acciones de funcionarios)",
                "5 años",
                "Trazabilidad de quién hizo qué — clave en investigaciones disciplinarias.",
              ],
              [
                "Credenciales (tokens WhatsApp, API keys, contraseñas hasheadas)",
                "Borrar inmediatamente",
                "Ya no son necesarias, son riesgo de seguridad.",
              ],
              [
                "Branding (logos, escudos)",
                "Borrar al cierre",
                "Propiedad del municipio — debe devolverse.",
              ],
            ]}
          />
          <div className="callout warn">
            <strong>Implicación práctica:</strong> &quot;eliminar un tenant&quot; en
            MiPueblo NO significa <code>DELETE FROM tenants WHERE id = X</code>.
            Significa: entregar PQRSDFs históricos al municipio + suspender
            accesos + borrar credenciales + (opcional, después de años)
            anonimizar datos personales.
          </div>
        </Section>

        {/* ---- Notificación ---- */}
        <Section title="3. Notificar al admin_tenant con anticipación">
          <p>
            Antes de tocar nada técnicamente, mandar notificación formal
            por email institucional al admin_tenant y al alcalde:
          </p>
          <Ol>
            <li>
              <strong>30 días antes</strong> del cierre planeado: primer
              aviso con fecha exacta de suspensión, qué van a perder
              acceso, y oferta de exportación de datos.
            </li>
            <li>
              <strong>15 días antes:</strong> recordatorio + confirmación
              de qué datos quieren exportar.
            </li>
            <li>
              <strong>3 días antes:</strong> aviso final con horario
              específico de corte.
            </li>
            <li>
              <strong>Día del cierre:</strong> confirmación post-suspensión.
            </li>
          </Ol>
          <p>
            Guardar todos los emails enviados como evidencia (la
            comunicación debe quedar en el audit log de la plataforma o en
            el sistema de tickets de MiPueblo).
          </p>
        </Section>

        {/* ---- Exportar ---- */}
        <Section title="4. Exportar los datos del tenant">
          <p>
            El cliente tiene derecho a llevarse <strong>todos sus datos</strong>{" "}
            en formato legible. Generar un dump completo:
          </p>
          <Ol>
            <li>
              Hacer un dump filtrado por <code>tenant_id</code> (NO el
              backup global):
              <pre className="code-block">{`# Reemplazar <UUID> con el ID real del tenant
sudo docker exec mipueblo-postgres pg_dump \\
  -U postgres -d mipueblo \\
  --format=plain \\
  --data-only \\
  --table=pqrsdfs --table=peticionarios --table=respuestas \\
  --table=conversaciones --table=mensajes \\
  --table=response_templates --table=kb_articles \\
  --table=usuarios_tenant --table=dependencias --table=audit_log \\
  | grep -E "(INSERT|--).*<UUID>" \\
  > /tmp/tenant-<slug>-export.sql`}</pre>
            </li>
            <li>
              Para los adjuntos en filesystem:
              <pre className="code-block">{`sudo tar czf /tmp/tenant-<slug>-media.tar.gz \\
  -C /var/lib/docker/volumes/mipueblo-media/_data \\
  <slug>/`}</pre>
            </li>
            <li>
              Generar el export en formatos amigables (CSV/Excel) usando el
              endpoint dedicado del panel:
              <pre className="code-block">{`POST /super/tenants/{tenant_id}/export
# Retorna un job_id; el zip queda listo en ~5 min para descarga`}</pre>
            </li>
            <li>
              Empaquetar todo en un ZIP cifrado (AES-256, password fuerte
              compartido por canal separado):
              <pre className="code-block">{`zip -e tenant-<slug>-full.zip \\
  /tmp/tenant-<slug>-export.sql \\
  /tmp/tenant-<slug>-media.tar.gz \\
  tenant-<slug>-pqrsdfs.csv`}</pre>
            </li>
            <li>
              Entregar al admin_tenant vía:
              <ul>
                <li>Link de descarga temporal (24-72h)</li>
                <li>Password por SMS o llamada a un funcionario</li>
                <li>Pedir confirmación de recepción y descarga exitosa</li>
              </ul>
            </li>
          </Ol>
          <div className="callout tip">
            💡 Incluye en el ZIP un <code>README.md</code> explicando el
            esquema de cada tabla/CSV, para que el nuevo proveedor o el
            equipo TIC del municipio sepa qué tienen.
          </div>
        </Section>

        {/* ---- Suspendido ---- */}
        <Section title="5. Período de gracia — pasar a suspended (30 días)">
          <p>
            Cambiar el tenant a estado <code>suspended</code> bloquea logins
            y desactiva canales, pero <strong>no borra nada</strong>. Es la
            red de seguridad por si el cliente cambia de opinión, o por si
            descubrimos que necesitan un dato más.
          </p>
          <Ol>
            <li>
              Panel super-admin → tenant → menú de estado →{" "}
              <Kbd>Suspender</Kbd>.
            </li>
            <li>
              Esto setea <code>tenant.status = &apos;suspended&apos;</code>{" "}
              y <code>suspendido_en = now()</code>.
            </li>
            <li>
              Efectos automáticos:
              <ul>
                <li>Funcionarios no pueden loguear (401 al intentar)</li>
                <li>El subdominio muestra una página de &quot;servicio suspendido&quot;</li>
                <li>WhatsApp deja de enviar (mensajes entrantes encolan pero no se procesan)</li>
                <li>Webhook URL queda activa pero el handler responde 503</li>
              </ul>
            </li>
            <li>
              Documentar en el audit log el motivo. Pueden hacerlo desde el
              panel — se guarda con <code>action=tenant.update</code>.
            </li>
          </Ol>
          <div className="callout tip">
            💡 Si después de 30 días el cliente quiere volver, basta cambiar
            de vuelta a <code>active</code> y todo funciona como antes. Por
            eso este paso es importante: la eliminación física no es
            reversible.
          </div>
        </Section>

        {/* ---- Cancelled ---- */}
        <Section title="6. Marcar como cancelled (definitivo lógico)">
          <p>
            Pasados los 30 días de gracia, si efectivamente cierra,
            cambiar a <code>cancelled</code>:
          </p>
          <Ol>
            <li>
              Panel → tenant → estado → <Kbd>Cancelar</Kbd>.
            </li>
            <li>
              Esto NO borra datos. Solo deja una marca de que el tenant ya
              no opera. Los PQRSDFs siguen ahí para auditoría.
            </li>
            <li>
              Endpoint:{" "}
              <code>PATCH /super/tenants/{`{id}`}</code> con{" "}
              <code>{`{"status": "cancelled"}`}</code>. Se guarda en audit
              log automáticamente.
            </li>
          </Ol>
        </Section>

        {/* ---- Eliminación física ---- */}
        <Section title="7. Eliminación física (años después, opcional)">
          <div className="callout warn">
            Solo aplicable cuando han pasado <strong>al menos 10 años</strong>{" "}
            desde el último PQRSDF radicado (cumplido el período de archivo
            general), Y el municipio ha confirmado transferencia al archivo
            histórico, Y hay autorización escrita firmada.
          </div>
          <p>
            En la mayoría de los casos esto NO se hace. Los datos del
            tenant cancelado se quedan en la DB para siempre — el costo de
            almacenamiento es trivial frente al riesgo legal de borrar mal.
          </p>
          <p>
            Si se hace, el proceso manual con verificación es:
          </p>
          <Ol>
            <li>
              Confirmar autorización escrita en archivo legal de MiPueblo.
            </li>
            <li>
              Tomar backup completo de la DB ANTES (paracaídas):{" "}
              <pre className="code-block">{`sudo /opt/mipueblo/api/scripts/backup-db.sh`}</pre>
            </li>
            <li>
              Confirmar que el tenant lleva mínimo 10 años en estado{" "}
              <code>cancelled</code> y no tiene PQRSDFs recientes:
              <pre className="code-block">{`SELECT MAX(created_at) FROM pqrsdfs WHERE tenant_id = '<UUID>';
SELECT suspendido_en, status FROM tenants WHERE id = '<UUID>';`}</pre>
            </li>
            <li>
              Confirmar que NO hay referencias activas en otras tablas que
              no hayan caído por cascada (revisar foreign keys del schema).
            </li>
            <li>
              Ejecutar borrado en transacción (RLS off como superuser, con
              cuidado):
              <pre className="code-block">{`BEGIN;
-- Las tablas con FK cascada al tenant se limpian solas
DELETE FROM tenants WHERE id = '<UUID>';
-- Verificar count antes de COMMIT
SELECT COUNT(*) FROM pqrsdfs WHERE tenant_id = '<UUID>';
-- Si todo OK:
COMMIT;
-- Si algo raro:
ROLLBACK;`}</pre>
            </li>
            <li>
              Borrar el directorio de adjuntos en filesystem:
              <pre className="code-block">{`sudo rm -rf /var/lib/docker/volumes/mipueblo-media/_data/<slug>/`}</pre>
            </li>
            <li>
              Registrar la eliminación en un log externo (fuera de la DB
              que se acaba de tocar) — ver punto 12.
            </li>
          </Ol>
        </Section>

        {/* ---- Adjuntos ---- */}
        <Section title="8. Adjuntos en filesystem">
          <p>
            Los archivos subidos (PDF de PQRSDFs, imágenes de WhatsApp,
            logos de branding) viven en el volumen docker{" "}
            <code>mipueblo-media</code>, montado en{" "}
            <code>/var/lib/mipueblo/media</code> dentro del container.
          </p>
          <p>Estructura:</p>
          <pre className="code-block">{`/var/lib/mipueblo/media/
  cajica/
    branding/
      logo.png
      escudo.svg
    pqrsdf/
      2026/05/abc-123/anexo1.pdf
      ...
    whatsapp/
      inbound/...
      outbound/...`}</pre>
          <p>
            <strong>Al pasar a suspended:</strong> nada que hacer, los
            archivos quedan inaccesibles porque la API rechaza requests.
          </p>
          <p>
            <strong>Al eliminación física:</strong> después de borrar las
            filas de DB, borrar también el directorio del tenant (paso 7.6).
            Si te lo saltas, quedan archivos huérfanos consumiendo disco
            sin estar referenciados.
          </p>
        </Section>

        {/* ---- Confirmación ---- */}
        <Section title="9. Confirmación formal con el cliente">
          <p>
            Antes de ejecutar pasos irreversibles (eliminación física), hay
            que tener:
          </p>
          <Checklist
            items={[
              <>
                <strong>Email institucional</strong> del alcalde o secretario
                general confirmando autorización de eliminación
              </>,
              <>
                <strong>Acta firmada</strong> (PDF) que incluya: nombre del
                tenant, slug, NIT, fecha de cierre, declaración de que el
                municipio recibió el export completo, autorización para
                eliminación de credenciales y datos personales (no del archivo
                histórico)
              </>,
              <>
                <strong>Constancia de recepción del export</strong>: el
                cliente debe responder por escrito que descargó y verificó el
                ZIP.
              </>,
              <>
                <strong>Aprobación interna de MiPueblo</strong>: super_admin
                líder + responsable legal firman antes de ejecutar.
              </>,
            ]}
          />
          <p>
            Estos documentos se archivan en el repositorio legal de MiPueblo
            (no en la DB del producto). Sirven para defenderse en caso de
            disputa futura.
          </p>
        </Section>

        {/* ---- Migración ---- */}
        <Section title="10. Migración a otro proveedor">
          <p>
            Si el cliente se va a otro proveedor de PQRSDF, lo que tenemos
            que entregarles para que puedan migrar limpio:
          </p>
          <Table
            head={["Entregable", "Formato", "Notas"]}
            rows={[
              [
                "Padrón de PQRSDFs",
                "CSV + JSON",
                "Una fila por PQRSDF con todos los campos, incluyendo radicado, fechas, estados, asignación, dependencia.",
              ],
              [
                "Respuestas y trazas",
                "CSV + PDF compilados",
                "Cada respuesta oficial firmada, con su radicado original.",
              ],
              [
                "Adjuntos",
                "Carpeta tar.gz con estructura por radicado",
                "Mantener nombre original del archivo + metadata de quién lo subió y cuándo.",
              ],
              [
                "Centro de ayuda",
                "Markdown export de cada artículo",
                "Para que puedan importarlo a su nueva plataforma o publicar en web.",
              ],
              [
                "Datos de peticionarios",
                "CSV (cifrado, password aparte)",
                "Nombre, contacto, identificación. SOLO si el cliente lo va a usar para continuidad de servicio.",
              ],
              [
                "Audit log de los últimos 5 años",
                "CSV",
                "Trazabilidad útil para investigaciones disciplinarias.",
              ],
              [
                "Plantillas de respuesta",
                "JSON / CSV",
                "Para reusar tono institucional ya validado.",
              ],
              [
                "Configuración de dependencias y horarios",
                "JSON",
                "Acelera setup en la nueva plataforma.",
              ],
            ]}
          />
          <p>
            <strong>Lo que NO entregamos:</strong>
          </p>
          <Ul
            items={[
              "Hashes de contraseñas de funcionarios (que se las re-creen)",
              "Tokens de WhatsApp / API keys (el cliente las regenera en su nuevo sistema)",
              "Logs internos de plataforma (errores técnicos, métricas)",
              "Código o esquema interno de MiPueblo (propiedad nuestra)",
            ]}
          />
        </Section>

        {/* ---- Auditoría ---- */}
        <Section title="11. Auditoría post-eliminación">
          <p>
            Toda acción sobre el tenant queda registrada en{" "}
            <code>audit_log</code> de la DB. Pero después de la eliminación
            física esa entrada también se va, así que hay que registrarla
            <strong> fuera</strong> de la DB del producto:
          </p>
          <Ol>
            <li>
              Crear una entrada en el log externo de auditoría de MiPueblo
              (puede ser un repo git privado, una bitácora, o sistema de
              tickets):
              <pre className="code-block">{`fecha: 2036-05-22
accion: eliminacion_fisica_tenant
tenant_slug: cajica
tenant_id: <UUID>
ejecutor: <super_admin email>
autorizacion: acta-2036-001.pdf (link)
export_entregado: 2036-05-15 (confirmado por el cliente)
backup_pre_borrado: mipueblo-20360522-...dump.xz (R2)
notas: ...`}</pre>
            </li>
            <li>
              Guardar referencia al backup pre-borrado en el log externo.
              Ese backup se retiene <strong>indefinidamente</strong> off-site
              (no se rota), por si en el futuro hay disputa o requerimiento
              judicial.
            </li>
            <li>
              Notificar internamente al equipo legal de MiPueblo con copia
              de toda la documentación.
            </li>
          </Ol>
        </Section>

        {/* ---- Checklist ---- */}
        <Section title="Checklist final antes de pasar a cancelled">
          <Checklist
            items={[
              "Aviso 30/15/3 días enviado al admin_tenant",
              "Export completo generado, descargado y confirmado por el cliente",
              "Acta de cierre firmada por alcalde o secretario general",
              "Tenant en suspended por al menos 30 días sin reclamos",
              "Credenciales (tokens WhatsApp, API keys IA) borradas/rotadas",
              "Branding (logos, escudos) entregado al municipio",
              "Audit log de los últimos 5 años entregado",
              "Aprobación interna firmada por super_admin + legal",
              "Entrada creada en bitácora externa de auditoría",
              "Cambio de estado a cancelled aplicado",
            ]}
          />
        </Section>

        {/* ---- Cierre ---- */}
        <Section title="Resumen rápido">
          <Table
            head={["Estado", "Reversible", "Datos accesibles"]}
            rows={[
              ["active", "—", "Sí, normal"],
              ["suspended", "Sí, instantáneo", "Datos en DB, accesos bloqueados"],
              ["cancelled", "Sí, requiere reactivación manual", "Datos en DB, no operativo"],
              ["eliminado físicamente", "NO (solo desde backup)", "Nada queda, solo el backup off-site"],
            ]}
          />
          <p style={{ marginTop: 16 }}>
            En la inmensa mayoría de los casos te quedas en{" "}
            <code>cancelled</code> para siempre. La eliminación física es la
            excepción rara que requiere proceso formal completo.
          </p>
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Checklist({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", display: "grid", gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              border: "1.5px solid var(--ink-3)",
              borderRadius: 4,
              marginTop: 3,
            }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: "var(--bone-warm)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 4,
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 12.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{ borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)" }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
